import { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Input, Heading, Spinner, Switch, Divider,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, useToast,
} from '@chakra-ui/react';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import {
  getOpeningHours,
  setOpeningHours,
  createSpecialDate,
  deleteSpecialDate,
  type OpeningHoursEntryDto,
  type SpecialDateHoursDto,
} from '../api/organizationApi';

const DAY_KEYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const;

function defaultWeeklySchedule(): OpeningHoursEntryDto[] {
  return Array.from({ length: 7 }, (_, i) => ({
    dayOfWeek: i,
    openTime: '09:00',
    closeTime: '18:00',
    isClosed: false,
  }));
}

export interface OpeningHoursFormProps {
  locationId: string;
}

export function OpeningHoursForm({ locationId }: OpeningHoursFormProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState<OpeningHoursEntryDto[]>(defaultWeeklySchedule());
  const [specialDates, setSpecialDates] = useState<SpecialDateHoursDto[]>([]);

  // New special date form state
  const [newDate, setNewDate] = useState('');
  const [newOpenTime, setNewOpenTime] = useState('09:00');
  const [newCloseTime, setNewCloseTime] = useState('18:00');
  const [newIsClosed, setNewIsClosed] = useState(false);
  const [newLabel, setNewLabel] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOpeningHours(locationId);
      if (data.weeklySchedule.length > 0) {
        // Ensure all 7 days are present
        const merged = defaultWeeklySchedule().map((day) => {
          const existing = data.weeklySchedule.find((e) => e.dayOfWeek === day.dayOfWeek);
          return existing ?? day;
        });
        setSchedule(merged);
      }
      setSpecialDates(data.specialDates);
    } catch {
      toast({ title: t('openingHours.loadError'), status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [locationId, t, toast]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveSchedule = async () => {
    try {
      setSaving(true);
      await setOpeningHours(locationId, { entries: schedule });
      toast({ title: t('openingHours.saved'), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('openingHours.saveError'), status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleAddSpecialDate = async () => {
    if (!newDate) return;
    try {
      const result = await createSpecialDate(locationId, {
        date: newDate,
        openTime: newIsClosed ? undefined : newOpenTime,
        closeTime: newIsClosed ? undefined : newCloseTime,
        isClosed: newIsClosed,
        label: newLabel || undefined,
      });
      setSpecialDates((prev) => [...prev, result]);
      setNewDate('');
      setNewLabel('');
      setNewIsClosed(false);
      toast({ title: t('openingHours.specialDateAdded'), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('openingHours.specialDateError'), status: 'error', duration: 3000 });
    }
  };

  const handleDeleteSpecialDate = async (id: string) => {
    try {
      await deleteSpecialDate(locationId, id);
      setSpecialDates((prev) => prev.filter((sd) => sd.id !== id));
      toast({ title: t('openingHours.specialDateDeleted'), status: 'success', duration: 2000 });
    } catch {
      toast({ title: t('openingHours.deleteError'), status: 'error', duration: 3000 });
    }
  };

  const updateDay = (dayOfWeek: number, field: keyof OpeningHoursEntryDto, value: string | boolean) => {
    setSchedule((prev) =>
      prev.map((entry) =>
        entry.dayOfWeek === dayOfWeek ? { ...entry, [field]: value } : entry,
      ),
    );
  };

  if (loading) {
    return (
      <Flex justify="center" p={6}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  return (
    <Box data-testid="opening-hours-form">
      <Heading as="h3" size="md" mb={4}>{t('openingHours.weeklySchedule')}</Heading>

      <Table variant="simple" size="sm" mb={4}>
        <Thead>
          <Tr>
            <Th>{t('openingHours.day')}</Th>
            <Th>{t('openingHours.open')}</Th>
            <Th>{t('openingHours.close')}</Th>
            <Th>{t('openingHours.closed')}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {schedule.map((entry) => (
            <Tr key={entry.dayOfWeek} data-testid={`day-row-${entry.dayOfWeek}`}>
              <Td fontWeight="500">
                {t(`openingHours.days.${DAY_KEYS[entry.dayOfWeek]}`)}
              </Td>
              <Td>
                <Input
                  type="time"
                  size="sm"
                  w="130px"
                  value={entry.openTime ?? ''}
                  onChange={(e) => updateDay(entry.dayOfWeek, 'openTime', e.target.value)}
                  isDisabled={entry.isClosed}
                />
              </Td>
              <Td>
                <Input
                  type="time"
                  size="sm"
                  w="130px"
                  value={entry.closeTime ?? ''}
                  onChange={(e) => updateDay(entry.dayOfWeek, 'closeTime', e.target.value)}
                  isDisabled={entry.isClosed}
                />
              </Td>
              <Td>
                <Switch
                  isChecked={entry.isClosed}
                  onChange={(e) => updateDay(entry.dayOfWeek, 'isClosed', e.target.checked)}
                  colorScheme="red"
                  data-testid={`day-closed-${entry.dayOfWeek}`}
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Flex justify="flex-end" mb={6}>
        <Button
          colorScheme="brand"
          onClick={handleSaveSchedule}
          isLoading={saving}
          data-testid="save-schedule-btn"
        >
          {t('openingHours.saveSchedule')}
        </Button>
      </Flex>

      <Divider my={4} />

      <Heading as="h3" size="md" mb={4}>{t('openingHours.specialDates')}</Heading>

      {specialDates.length > 0 && (
        <Table variant="simple" size="sm" mb={4}>
          <Thead>
            <Tr>
              <Th>{t('openingHours.date')}</Th>
              <Th>{t('openingHours.label')}</Th>
              <Th>{t('openingHours.open')}</Th>
              <Th>{t('openingHours.close')}</Th>
              <Th>{t('openingHours.closed')}</Th>
              <Th w="50px"></Th>
            </Tr>
          </Thead>
          <Tbody>
            {specialDates.map((sd) => (
              <Tr key={sd.id} data-testid={`special-date-${sd.id}`}>
                <Td>{sd.date}</Td>
                <Td>{sd.label ?? '—'}</Td>
                <Td>{sd.isClosed ? '—' : sd.openTime}</Td>
                <Td>{sd.isClosed ? '—' : sd.closeTime}</Td>
                <Td>{sd.isClosed ? t('openingHours.closedLabel') : ''}</Td>
                <Td>
                  <IconButton
                    aria-label={t('common.delete')}
                    icon={<DeleteIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => sd.id && handleDeleteSpecialDate(sd.id)}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Box p={4} borderRadius="md" bg="gray.50" mb={3}>
        <Text fontWeight="600" mb={2}>{t('openingHours.addSpecialDate')}</Text>
        <Flex gap={3} wrap="wrap" align="flex-end">
          <Box>
            <Text fontSize="sm" mb={1}>{t('openingHours.date')}</Text>
            <Input
              type="date"
              size="sm"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              data-testid="new-special-date"
            />
          </Box>
          <Box>
            <Text fontSize="sm" mb={1}>{t('openingHours.label')}</Text>
            <Input
              size="sm"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={t('openingHours.labelPlaceholder')}
              data-testid="new-special-label"
            />
          </Box>
          {!newIsClosed && (
            <>
              <Box>
                <Text fontSize="sm" mb={1}>{t('openingHours.open')}</Text>
                <Input
                  type="time"
                  size="sm"
                  value={newOpenTime}
                  onChange={(e) => setNewOpenTime(e.target.value)}
                />
              </Box>
              <Box>
                <Text fontSize="sm" mb={1}>{t('openingHours.close')}</Text>
                <Input
                  type="time"
                  size="sm"
                  value={newCloseTime}
                  onChange={(e) => setNewCloseTime(e.target.value)}
                />
              </Box>
            </>
          )}
          <Flex align="center" gap={2}>
            <Text fontSize="sm">{t('openingHours.closed')}</Text>
            <Switch
              isChecked={newIsClosed}
              onChange={(e) => setNewIsClosed(e.target.checked)}
              colorScheme="red"
            />
          </Flex>
          <Button
            size="sm"
            colorScheme="brand"
            leftIcon={<AddIcon />}
            onClick={handleAddSpecialDate}
            isDisabled={!newDate}
            data-testid="add-special-date-btn"
          >
            {t('common.add')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
}
